import {
  Button,
  Checkbox,
  Drawer,
  Hairline,
  Label,
  Loading,
  Message,
  Title,
} from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import CampaignDetails from "./CampaignDetails";
import { Currency } from "shared/utils/Currency";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { useEffect, useRef, useState } from "react";
import { ReportsAPI } from "providers/ReportsAPI";
import dayjs from "dayjs";
import { Numbers } from "shared/utils/Numbers";
import Filters from "pages/Reports/Filters";
import ReportFilters from "pages/Reports/ReportFilters";
import { Trend } from "pages/Reports/Reports";
import { If } from "shared/components/control-statements";
import { InputSearch } from "shared/components/InputSearch";
import classNames from "classnames/bind";
import FilterColumns from "./FiltersColumns";
import { date } from "yup";
import { useLocation, useNavigate } from "react-router";
const css = classNames.bind(styles);

const BigNumberTitles = {
  total: "Faturamento influenciado",
  sendCost: "Custos com disparos",
  sends: "Quantidade de disparos",
  discounts: "Descontos totais",
  totalWithDiscount: "Faturamento com cupons",
  roi: "ROI Geral",
};

const BigNumber = ({ data, onClick }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
          {/* <Trend growth={data.growth} /> */}
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <span className={styles.info} title={data.tooltip}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
      <Button design="default-icon-mini-2" onClick={onClick}>
        <i className="far fa-chart-waterfall" />
      </Button>
    </div>
  );
};

const numbers = (data, isBeforeSeptember) => {
  if (!data) return {};

  return [
    {
      title: "Faturamento influenciado",
      value: Currency.formatCurrency(data?.total?.value || 0),
      growth: data.total?.growth,
      alias: "total",
      tooltip:
        "Soma do valor de todos os pedidos realizados por clientes atingidos por campanhas em até 5 dias.",
    },
    {
      title: "Faturamento com cupons",
      value: Currency.formatCurrency(data.totalWithDiscount?.value),
      growth: data.totalWithDiscount?.growth,
      alias: "totalWithDiscount",
      tooltip:
        "Soma do valor de todos os pedidos utilizando cupons vinculados à campanhas.",
    },
    {
      title: "Descontos totais",
      value: isBeforeSeptember
        ? "---"
        : Currency.formatCurrency(data.discounts?.value),
      growth: data.discounts?.growth,
      alias: "discounts",
      tooltip:
        "Soma do valor de todos os descontos em pedidos que utilizaram cupons vinculados à campanhas.",
    },
    {
      title: "Custos com disparos",
      value: Currency.formatCurrency(data.sendCost?.value),
      growth: data.sendCost?.growth,
      alias: "sendCost",
      tooltip: "Valor total gasto com mensageria.",
    },
    {
      title: "Quantidade de disparos",
      value: Numbers.formatInt(data.sends?.value),
      growth: data.sends?.growth,
      alias: "sends",
      tooltip: "Quantidade de mensagens disparadas utilizando mensageria.",
    },
    {
      title: "ROI geral",
      value: isBeforeSeptember ? "---" : Numbers.formatInt(data.roi?.value),
      growth: data.roi?.growth,
      alias: "roi",
      tooltip:
        "Retorno sobre o investimento de campanhas. FÓRMULA: {{ ((Faturamento total - (Descontos totais + Custos com disparos)) / (Descontos totais + Custos de disparos)) * 100 }}.",
    },
  ];
};

const columns = [
  "NOME",
  "STATUS",
  "DATA DE CRIAÇÃO",
  "ÚLTIMO DISPARO",
  "CANAL",
  "FATURAMENTO INFLUENCIADO",
  "FATURAMENTO COM CUPOM",
  "FATURAMENTO TOTAL",
  "CUSTO DO DISPARO",
  "DESCONTOS COM CUPOM",
  "CUSTO TOTAL",
  "ROI CONSERVADOR",
  "ROI TOTAL",
  "CUPONS DISTRIBUÍDOS",
  "QTD. DISPAROS",
  "QTD. ENTREGAS",
  "QTD. ABERTURAS",
  "QTD. CLIQUES",
  "QTD. USOS",
];

export default function Index() {
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    flows: [],
  });
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [openDateFilters, setOpenDateFilters] = useState(false);
  const [openHiddenColumns, setOpenHiddenColumns] = useState(false);
  const [openFilterColumns, setOpenFilterColumns] = useState(false);
  const [tempHiddenColumns, setTempHiddenColumns] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [searchColumns, setSearchColumns] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [search, setSearch] = useState("");

  const filtersRef = useRef(null);

  useEffect(() => {
    const filters = sessionStorage.getItem("filters");
    const dateFilters = sessionStorage.getItem("dateFilters");
    const reportFilters = sessionStorage.getItem("reportFilters");
    const hiddenColumns = sessionStorage.getItem("hiddenColumns");
    if (filters) setAppliedFilters(JSON.parse(filters));
    if (dateFilters) setDateFilters(JSON.parse(dateFilters));
    if (reportFilters) setFilters(JSON.parse(reportFilters));
    if (hiddenColumns) {
      setHiddenColumns(JSON.parse(hiddenColumns));
      setTempHiddenColumns(JSON.parse(hiddenColumns));
    }
  }, []);

  const pathname = useLocation().pathname;

  useEffect(() => {
    const content = document.getElementById("content");
    content.style.overflowY = "hidden";
  }, [pathname]);

  useEffect(() => {
    sessionStorage.setItem("filters", JSON.stringify(appliedFilters));
  }, [appliedFilters]);

  useEffect(() => {
    sessionStorage.setItem("dateFilters", JSON.stringify(dateFilters));
  }, [dateFilters]);

  useEffect(() => {
    sessionStorage.setItem("reportFilters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    sessionStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const fetchData = async () => {
    setLoading(true);

    const provider = new ReportsAPI();
    const response = await provider.flow(filters);

    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const september2025 = dayjs("2025-09-01T00:00:00.000Z");

  const rangeBeforeSeptember =
    dayjs(filters.start_date).isBefore(september2025) ||
    dayjs(filters.end_date).isBefore(september2025);
  const currentBeforeSeptember = dayjs().isBefore(september2025, "month");

  const isBeforeSeptember = rangeBeforeSeptember;

  const fetchHistory = async (payload) => {
    setLoadingHistoric(true);

    const { year, month } = payload;
    const provider = new ReportsAPI();

    const results = await provider.flowHistory("keynumbers", year, month);

    const limit = month ? dayjs(`${year}-${month}`).endOf("month").date() : 12;
    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      const parsedDate = month
        ? dayjs(result.date, "DD/MM/YYYY")
        : dayjs(result.month, "MM/YYYY");

      const index = month ? parsedDate.date() : parsedDate.month() + 1;

      if (index >= 1 && index <= limit) {
        const data = result[selectedData.alias];

        rows[index - 1] = {
          value: data?.value ?? 0,
          growth: data?.growth ?? 0,
        };
      }
    }

    setLoadingHistoric(false);

    return rows;
  };

  const filterCampaignsByDateAndChannel = () => {
    const {
      createdDate,
      channels,
      status = {
        DRAFT: true,
        PAUSED: true,
        ARCHIVED: false,
        PUBLISHED: true,
      },
    } = appliedFilters;

    if (!data.flows || data.flows.length === 0) return;

    return data.flows
      .filter((d) => (status[d.state] ? d : null))
      .map((campaign) => {
        const allowedCreatives = campaign.creatives.filter(
          (c) => !channels?.includes(c.type)
        );

        if (!allowedCreatives.length) return null;

        if (
          !(campaign?.name).toLowerCase().includes(search.toLowerCase()) &&
          !String(campaign?.id).includes(search.toLowerCase())
        )
          return null;

        const inDateRange =
          createdDate?.start_date && createdDate?.end_date
            ? new Date(campaign.created_at) >=
                new Date(createdDate.start_date) &&
              new Date(campaign.created_at) <= new Date(createdDate.end_date)
            : true;

        if (!inDateRange) return null;

        return {
          ...campaign,
          creatives: allowedCreatives,
        };
      })
      .filter(Boolean);
  };

  let filteredCampaigns = filterCampaignsByDateAndChannel();

  const isVisible = (column) => !hiddenColumns.includes(column);

  const columnsObject = columns.reduce((acc, column) => {
    const name = column.toLowerCase();
    const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
    acc[column] = { name: formattedName, value: column };
    return acc;
  }, {});

  const filteredColumns = Object.values(columnsObject).filter((c) => {
    return searchColumns
      ? c.name?.toLowerCase().includes(searchColumns.toLowerCase())
      : true;
  });

  const navigate = useNavigate();

  return (
    <>
      <Title
        title="Relatórios de Campanhas"
        backTo={{ url: "/relatorios/marketing", name: "Marketing" }}
      >
        <div className="d-flex align-center">
          <Filters
            onRemoveFilter={(filter) => {
              filtersRef.current?.removeFilter(filter);
            }}
            filters={filters}
          />
          <If cond={Object.entries(appliedFilters).length > 0}>
            <div className={styles.tagFilter}>
              <span>
                {Object.entries(appliedFilters).length}{" "}
                {Object.entries(appliedFilters).length > 1
                  ? "filtros aplicados"
                  : "filtro aplicado"}
              </span>
              <div
                className={styles.remove}
                onClick={() => setAppliedFilters({})}
              >
                <i className="fal fa-times" />
              </div>
            </div>
          </If>
          <If cond={hiddenColumns.length > 0}>
            <div className={styles.tagFilter}>
              <i className="fal fa-eye" />
              <span>{columns.length - hiddenColumns.length} colunas</span>
              <div
                className={styles.remove}
                onClick={() => {
                  setHiddenColumns([]);
                  setTempHiddenColumns([]);
                }}
              >
                <i className="fal fa-times" />
              </div>
            </div>
          </If>

          <InputSearch
            design="two"
            placeholder="Buscar por..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.divider} />
          <Button
            design="default-icon-mini-2"
            onClick={() => setOpenHiddenColumns(true)}
            dot={hiddenColumns.length > 0}
          >
            <i className="fal fa-eye" />
          </Button>
          {/* <Button
            design="default-icon-mini-2"
            onClick={() => {
              const ids = filteredCampaigns.map((c) => c.id);
              navigate(`/relatorios/campanhas?ids=${ids.join(",")}`);
            }}
            className="ml-10"
          >
            <i className="fal fa-chart-waterfall" />
          </Button> */}
        </div>

        <Button onClick={() => setOpenFilters(true)} className="ml-10">
          Filtrar
        </Button>
      </Title>

      {loading ? (
        <Loading fullContent />
      ) : (
        <>
          <If cond={isBeforeSeptember}>
            <Message type="warning" className="mb-15">
              Devido a data filtrada, os seguintes dados não serão exibidos por
              falta de registro: Descontos com cupom, Descontos totais, ROI com
              cupom e ROI Geral
            </Message>
          </If>

          <CampaignDetails
            data={filteredCampaigns?.filter((c) => {
              return c;
            })}
            columns={columns}
            columnsObject={columnsObject}
            filteredColumns={filteredColumns}
            isVisible={isVisible}
            hiddenColumns={hiddenColumns}
            search={search}
            filters={filters}
          />
        </>
      )}

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          onFetchData={fetchHistory}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
        />
      </Drawer>

      <Drawer
        visible={openFilters}
        onClose={() => setOpenFilters(false)}
        title="Filtrar"
      >
        <div className={styles.filterOptions}>
          <div
            className={styles.option}
            onClick={() => setOpenDateFilters(true)}
          >
            <div className={styles.icon}>
              <i className="fal fa-calendar-day" />
            </div>
            <p>Filtro de dados</p>
          </div>
          <div
            className={styles.option}
            onClick={() => setOpenFilterColumns(true)}
          >
            <div className={styles.icon}>
              <i className="fal fa-filter" />
            </div>
            <p>Filtro de visualização</p>
          </div>
        </div>
      </Drawer>

      <ReportFilters
        ref={filtersRef}
        open={openDateFilters}
        onClose={() => {
          setOpenFilters(false);
          setOpenDateFilters(false);
        }}
        dateFiltersProp={dateFilters}
        onUpdateFilters={(filters) => {
          setFilters(filters);
          sessionStorage.setItem("reportFilters", JSON.stringify(filters));
        }}
        hideBrands
        hideOrigins
        allowOnlyMonth
        allowIntervalAfter={dayjs("2025-09-01")}
        hideHours={true}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
          sessionStorage.setItem("dateFilters", JSON.stringify(filters));
        }}
        withoutTransition
      />

      <Drawer
        visible={openHiddenColumns}
        onClose={() => setOpenHiddenColumns(false)}
        title="Exibição de colunas"
      >
        <Label>Selecione quais colunas deseja exibir:</Label>
        <InputSearch
          placeholder="Buscar por..."
          design="three"
          onChange={(e) => setSearchColumns(e.target.value)}
        />
        <Hairline className="mt-20 mb-20" />
        <div className={styles.hiddenColumns}>
          {(filteredColumns || []).map((column) => (
            <div
              key={column.value}
              className={css({
                column: true,
                hidden: tempHiddenColumns.includes(column.value),
              })}
              onClick={() =>
                setTempHiddenColumns((prev) =>
                  prev.includes(column.value)
                    ? prev.filter((c) => c !== column.value)
                    : [...prev, column.value]
                )
              }
            >
              <Checkbox
                checked={!tempHiddenColumns.includes(column.value)}
                readOnly
              />
              <div className="d-flex align-center">
                <span className={styles.divider} />
                <p className={styles.name}>{column.name}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.hiddenColumnsFooter}>
          <Button
            className="w-100"
            design="primary"
            onClick={() => {
              setHiddenColumns(tempHiddenColumns);
              setOpenHiddenColumns(false);
            }}
          >
            Aplicar
          </Button>
        </div>
      </Drawer>

      <FilterColumns
        propFilters={appliedFilters}
        visible={openFilterColumns}
        onClose={() => {
          setOpenFilters(false);
          setOpenFilterColumns(false);
        }}
        onApplyFilters={(filters) => {
          sessionStorage.setItem("filters", JSON.stringify(filters));
          setAppliedFilters(filters);
        }}
        withoutTransition
      />
    </>
  );
}
